import React from "react";
import { Box, Typography, Button, IconButton, Divider } from "@mui/material";
import { Close, Refresh } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { errorImage } from "assets";
import { ErrorPopoverProps } from "type";

const PopoverContainer = styled(Box)(({ theme }) => ({
  position: "fixed",
  bottom: 0,
  left: 0,
  right: 0,
  borderTopLeftRadius: "var(--radius-error-popover)",
  borderTopRightRadius: "var(--radius-error-popover)",
  borderTop: "1px solid var(--color-error-border)",
  padding: "var(--spacing-error-popover)",
  zIndex: "var(--z-error-popover)",
  boxShadow: `0 -4px 20px var(--color-error-shadow)`,
  textAlign: "center",

  // Mobile default
  width: "100%",
  maxWidth: "100%",
  margin: 0,
  borderBottomLeftRadius: 0,
  borderBottomRightRadius: 0,
  animation: "slideUpMobile var(--transition-error-slide)",

  "@keyframes slideUpMobile": {
    from: {
      transform: "translateY(100%)",
      opacity: 0,
    },
    to: {
      transform: "translateY(0)",
      opacity: 1,
    },
  },

  // For larger screens (sm and up)
  [theme.breakpoints.up("sm")]: {
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    bottom: "unset",
    right: "unset",
    width: "100%",
    maxWidth: "400px",
    margin: "0 auto",
    borderRadius: "var(--radius-error-popover)",
    border: "1px solid var(--color-error-border)",
    animation: "fadeInCenter 0.4s ease-out",

    "@keyframes fadeInCenter": {
      from: {
        opacity: 0,
      },
      to: {
        opacity: 1,
      },
    },
  },
}));

const CloseButton = styled(IconButton)({
  position: "absolute",
  top: "var(--spacing-error-close-top)",
  right: "var(--spacing-error-close-right)",
  color: "var(--color-error-text-primary)",
  padding: "var(--spacing-error-close-padding)",
  "&:hover": {
    backgroundColor: "var(--color-error-hover-bg)",
  },
});

const Title = styled(Typography)({
  color: "var(--color-error-text-primary)",
  fontSize: "var(--font-error-title-size)",
  fontWeight: "var(--font-error-title-weight)",
  marginBottom: "var(--spacing-error-title-mb)",
  textAlign: "center",
  fontFamily: "var(--font-family-primary)",
});

const Content = styled(Typography)({
  color: "var(--color-error-text-secondary)",
  fontSize: "var(--font-error-content-size)",
  fontWeight: "var(--font-error-content-weight)",
  lineHeight: "var(--font-error-content-line)",
  marginBottom: "var(--spacing-error-content-mb)",
  textAlign: "center",
  fontFamily: "var(--font-family-primary)",
});

const StyledDivider = styled(Divider)({
  border: `1px solid var(--color-error-divider)`,
  margin: "var(--spacing-error-divider) 0",
  width: "100%",
});

const RetryButton = styled(Button)({
  background: "var(--bg-error-btn-gradient)",
  color: "var(--color-error-text-black)",
  fontWeight: "var(--font-error-btn-weight)",
  fontSize: "var(--font-error-btn-size)",
  textTransform: "none",
  borderRadius: "var(--radius-error-btn)",
  padding:
    "var(--spacing-error-btn-vertical) var(--spacing-error-btn-horizontal)",
  width: "100%",
  display: "flex",
  gap: "var(--spacing-error-btn-gap)",
  fontFamily: "var(--font-family-primary)",
  transition: "var(--transition-fast)",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: `0 4px 12px var(--color-primary-shadow)`,
  },
  clipPath: "polygon(0% 0%, 100% 0%, 100% 50%, 93% 98%, 19% 100%, 0% 100%)",
  borderBottomRightRadius: "37px",
});

const RefreshIcon = styled(Refresh)({
  fontSize: "var(--icon-error-refresh)",
});

const ErrorImage = styled("img")({
  width: "var(--error-icon-size-wd)",
  height: "var(--error-icon-size-hg)",
  marginBottom: "var(--error-spacing-sm)",
});

const ErrorPopover: React.FC<ErrorPopoverProps> = ({
  isOpen,
  onClose,
  onRetry,
  title,
  content,
}) => {
  if (!isOpen) return null;

  return (
    <PopoverContainer>
      <CloseButton onClick={onClose} aria-label="Close error popover">
        <Close />
      </CloseButton>
      <ErrorImage src={errorImage} alt="Error" />
      <Title variant="h5">{title}</Title>
      <Content>{content}</Content>
      <StyledDivider />
      <RetryButton onClick={onRetry} aria-label="Retry operation">
        Retry
        <RefreshIcon />
      </RetryButton>
    </PopoverContainer>
  );
};

export default ErrorPopover;
